import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  deleteField,
  query,
  where,
  orderBy,
  limit,
  writeBatch,
  serverTimestamp,
  Timestamp,
  onSnapshot
} from 'firebase/firestore';
import { db, auth, currentCompanyName } from '../firebase/config';
import firebaseFunctions from './firebaseFunctions';

// Helper to inject companyId into queries
const companyQuery = (colRef) => {
  return query(colRef, where('companyId', '==', currentCompanyName));
};

// Helper to inject companyId into documents
const withCompany = (data) => {
  return { ...data, companyId: currentCompanyName };
};

// Audit log helper (best-effort; does not throw)
const logAudit = async ({ action, entityType, entityId, details }) => {
  try {
    const user = auth.currentUser;
    await addDoc(collection(db, COLLECTIONS.AUDIT_LOG), {
      companyId: currentCompanyName,
      action,
      entityType: entityType || null,
      entityId: entityId || null,
      details: details || null,
      userId: user?.uid || null,
      userEmail: user?.email || null,
      createdAt: serverTimestamp()
    });
  } catch (e) {
    console.warn('[Audit] Failed to write audit log:', e);
  }
};

// Collection names
const COLLECTIONS = {
  VEHICLES: 'vehicles',
  INVOICES: 'invoices',
  GARAGE_INFO: 'garageInfo',
  USERS: 'users',
  SETTINGS: 'settings',
  PROMOTIONS: 'promotions',
  LINKS: 'links',
  AUDIT_LOG: 'auditLog'
};

/**
 * Vehicles Service
 */
export const vehiclesService = {
  // Get all vehicles
  async getAll() {
    try {
      const vehiclesRef = collection(db, COLLECTIONS.VEHICLES);
      const snapshot = await getDocs(companyQuery(vehiclesRef));
      if (snapshot.empty && process.env.NODE_ENV === 'development') {
        console.warn('[Firestore] No vehicles. Check: 1) projectId matches .env 2) REACT_APP_ACTIVE_COMPANY matches tenant 3) users/<uid> companyId matches');
      }
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      throw error;
    }
  },

  /**
   * Subscribe to all vehicles for this company (real-time updates).
   * @param {(vehicles: Array) => void} onUpdate - called with vehicle array on each change
   * @param {(err: Error) => void} onError - optional error handler
   * @returns {() => void} unsubscribe function
   */
  subscribeAll(onUpdate, onError) {
    const vehiclesRef = collection(db, COLLECTIONS.VEHICLES);
    const q = companyQuery(vehiclesRef);
    return onSnapshot(
      q,
      (snapshot) => {
        const vehicles = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        onUpdate(vehicles);
      },
      (err) => {
        console.error('Vehicles subscription error:', err);
        if (onError) onError(err);
      }
    );
  },

  // Get a single vehicle by ID
  async getById(id) {
    try {
      const vehicleRef = doc(db, COLLECTIONS.VEHICLES, id);
      const snapshot = await getDoc(vehicleRef);
      if (snapshot.exists()) {
        const data = snapshot.data();
        if (data.companyId !== currentCompanyName) return null;
        return { id: snapshot.id, ...data };
      }
      return null;
    } catch (error) {
      console.error('Error fetching vehicle:', error);
      throw error;
    }
  },

  // Add a new vehicle
  async add(vehicle) {
    try {
      const vehiclesRef = collection(db, COLLECTIONS.VEHICLES);
      const docRef = await addDoc(vehiclesRef, {
        ...withCompany(vehicle),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      await logAudit({ action: 'create', entityType: 'vehicle', entityId: docRef.id, details: { reg: vehicle.reg } });
      return { id: docRef.id, ...vehicle };
    } catch (error) {
      console.error('Error adding vehicle:', error);
      throw error;
    }
  },

  // Update a vehicle
  async update(id, updates) {
    try {
      const vehicleRef = doc(db, COLLECTIONS.VEHICLES, id);
      await updateDoc(vehicleRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
      await logAudit({ action: 'update', entityType: 'vehicle', entityId: id, details: { keys: Object.keys(updates) } });
      return { id, ...updates };
    } catch (error) {
      console.error('Error updating vehicle:', error);
      throw error;
    }
  },

  // Soft-delete a vehicle (set deletedAt; vehicle remains in Firestore and appears in Archive)
  async delete(id) {
    try {
      const vehicleRef = doc(db, COLLECTIONS.VEHICLES, id);
      await updateDoc(vehicleRef, {
        deletedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      await logAudit({ action: 'soft_delete', entityType: 'vehicle', entityId: id });
    } catch (error) {
      console.error('Error deleting vehicle:', error);
      throw error;
    }
  },

  // Restore a soft-deleted vehicle back into the active garage
  async restore(id) {
    try {
      const vehicleRef = doc(db, COLLECTIONS.VEHICLES, id);
      const snap = await getDoc(vehicleRef);
      if (!snap.exists()) {
        throw new Error('Vehicle not found');
      }
      const data = snap.data() || {};
      const updates = {
        // Remove the field entirely so saves / filters treat it as not deleted
        deletedAt: deleteField(),
        updatedAt: serverTimestamp()
      };
      // Sold / archive status must return to a workshop stage
      if (data.status === 'Archive') {
        updates.status = data.previousStatus || 'Workshop';
      }
      // Garage hides paid+collected jobs — clear collected so restored cars reappear
      if (data.paid && data.collected) {
        updates.collected = false;
      }
      await updateDoc(vehicleRef, updates);
      await logAudit({ action: 'restore', entityType: 'vehicle', entityId: id });
      return {
        id,
        status: updates.status || data.status,
        collected: updates.collected === false ? false : data.collected
      };
    } catch (error) {
      console.error('Error restoring vehicle:', error);
      throw error;
    }
  },

  // Hard-delete a vehicle (use only for cleanup/demo; normal flow uses soft delete)
  async hardDelete(id) {
    try {
      const vehicleRef = doc(db, COLLECTIONS.VEHICLES, id);
      await deleteDoc(vehicleRef);
    } catch (error) {
      console.error('Error hard-deleting vehicle:', error);
      throw error;
    }
  },

  /**
   * Batch save vehicles - single round-trip instead of N sequential writes.
   * Much faster when updating multiple vehicles.
   */
  async batchSave(vehicles, existingById) {
    const vehiclesRef = collection(db, COLLECTIONS.VEHICLES);
    const localIds = new Set(vehicles.filter(v => v.id).map(v => v.id));
    const BATCH_LIMIT = 500;

    const ops = [];
    for (const vehicle of vehicles) {
      if (vehicle.id && existingById.has(vehicle.id)) {
        // Never write deletedAt via batch save — only delete()/restore() manage it.
        // Otherwise a stale local deletedAt can undo a restore (or clear a soft-delete).
        const { id, createdAt, updatedAt, deletedAt, ...data } = vehicle;
        ops.push({ type: 'update', id, data });
      } else if (!vehicle.id) {
        const { deletedAt, ...safeVehicle } = vehicle;
        ops.push({ type: 'add', vehicle: safeVehicle });
      }
    }
    for (const [id] of existingById) {
      if (!localIds.has(id)) ops.push({ type: 'delete', id });
    }

    for (let i = 0; i < ops.length; i += BATCH_LIMIT) {
      const batch = writeBatch(db);
      const chunk = ops.slice(i, i + BATCH_LIMIT);
      for (const op of chunk) {
        if (op.type === 'update') {
          const ref = doc(vehiclesRef, op.id);
          batch.update(ref, { ...op.data, updatedAt: serverTimestamp() });
        } else if (op.type === 'add') {
          const ref = doc(vehiclesRef);
          batch.set(ref, {
            ...withCompany(op.vehicle),
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });
        } else if (op.type === 'delete') {
          const ref = doc(vehiclesRef, op.id);
          batch.update(ref, {
            deletedAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });
        }
      }
      await batch.commit();
    }
  },

  // Batch add vehicles
  async batchAdd(vehicles) {
    try {
      const batch = writeBatch(db);
      const vehiclesRef = collection(db, COLLECTIONS.VEHICLES);
      
      vehicles.forEach(vehicle => {
        const docRef = doc(vehiclesRef);
        batch.set(docRef, {
          ...withCompany(vehicle),
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      });

      await batch.commit();
      return vehicles.length;
    } catch (error) {
      console.error('Error batch adding vehicles:', error);
      throw error;
    }
  },

  // Delete all vehicles (batch delete)
  async deleteAll() {
    try {
      const vehiclesRef = collection(db, COLLECTIONS.VEHICLES);
      const snapshot = await getDocs(companyQuery(vehiclesRef));
      
      // Firestore batch limit is 500, so we need to process in batches
      const batchSize = 500;
      const docs = snapshot.docs;
      let deletedCount = 0;
      
      for (let i = 0; i < docs.length; i += batchSize) {
        const batch = writeBatch(db);
        const batchDocs = docs.slice(i, i + batchSize);
        
        batchDocs.forEach((docSnapshot) => {
          batch.delete(docSnapshot.ref);
        });
        
        await batch.commit();
        deletedCount += batchDocs.length;
      }
      
      return deletedCount;
    } catch (error) {
      console.error('Error deleting all vehicles:', error);
      throw error;
    }
  }
};

/**
 * Invoices Service
 */
export const invoicesService = {
  // Helper function to convert Firestore Timestamp to ISO string
  convertTimestampToString(value) {
    if (value && value.toDate && typeof value.toDate === 'function') {
      // It's a Firestore Timestamp
      return value.toDate().toISOString();
    }
    if (value instanceof Date) {
      return value.toISOString();
    }
    // Already a string or other type
    return value;
  },

  // Get all invoices
  async getAll() {
    try {
      const invoicesRef = collection(db, COLLECTIONS.INVOICES);
      const snapshot = await getDocs(
        query(companyQuery(invoicesRef), orderBy('date', 'desc'))
      );
      return snapshot.docs.map(doc => {
        const data = doc.data();
        // Convert date field from Timestamp to ISO string if needed
        if (data.date) {
          data.date = this.convertTimestampToString(data.date);
        }
        return {
          id: doc.id,
          ...data
        };
      });
    } catch (error) {
      console.error('Error fetching invoices:', error);
      throw error;
    }
  },

  // Get invoices from the last year
  async getRecent() {
    try {
      const oneYearAgo = new Date();
      oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
      oneYearAgo.setHours(0, 0, 0, 0);

      const invoicesRef = collection(db, COLLECTIONS.INVOICES);
      const q = query(
        invoicesRef,
        where('companyId', '==', currentCompanyName),
        where('date', '>=', Timestamp.fromDate(oneYearAgo)),
        orderBy('date', 'desc')
      );
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => {
        const data = doc.data();
        // Convert date field from Timestamp to ISO string if needed
        if (data.date) {
          data.date = this.convertTimestampToString(data.date);
        }
        return {
          id: doc.id,
          ...data
        };
      });
    } catch (error) {
      console.error('Error fetching recent invoices:', error);
      // If query fails (no index), fall back to getAll and filter client-side
      const allInvoices = await this.getAll();
      const oneYearAgo = new Date();
      oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
      oneYearAgo.setHours(0, 0, 0, 0);
      
      return allInvoices.filter(inv => {
        const invDate = new Date(inv.date);
        invDate.setHours(0, 0, 0, 0);
        return invDate >= oneYearAgo;
      }).sort((a, b) => new Date(b.date) - new Date(a.date)); // Ensure descending order
    }
  },

  /**
   * Subscribe to recent invoices (last year) for real-time updates.
   * @param {(invoices: Array) => void} onUpdate - called with invoice array on each change
   * @param {(err: Error) => void} onError - optional error handler
   * @returns {() => void} unsubscribe function
   */
  subscribeRecent(onUpdate, onError) {
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
    oneYearAgo.setHours(0, 0, 0, 0);

    const invoicesRef = collection(db, COLLECTIONS.INVOICES);
    const q = query(
      invoicesRef,
      where('companyId', '==', currentCompanyName),
      where('date', '>=', Timestamp.fromDate(oneYearAgo)),
      orderBy('date', 'desc')
    );

    return onSnapshot(
      q,
      (snapshot) => {
        const invoices = snapshot.docs.map((d) => {
          const data = d.data();
          if (data.date) data.date = this.convertTimestampToString(data.date);
          return { id: d.id, ...data };
        });
        onUpdate(invoices);
      },
      (err) => {
        console.error('Invoices subscription error:', err);
        if (onError) onError(err);
      }
    );
  },

  // Get a single invoice by ID
  async getById(id) {
    try {
      const invoiceRef = doc(db, COLLECTIONS.INVOICES, id);
      const snapshot = await getDoc(invoiceRef);
      if (snapshot.exists()) {
        const data = snapshot.data();
        if (data.companyId !== currentCompanyName) return null;
        return { id: snapshot.id, ...data };
      }
      return null;
    } catch (error) {
      console.error('Error fetching invoice:', error);
      throw error;
    }
  },

  // Add a new invoice
  async add(invoice) {
    try {
      const invoicesRef = collection(db, COLLECTIONS.INVOICES);
      const invoiceData = {
        ...withCompany(invoice),
        date: invoice.date ? Timestamp.fromDate(new Date(invoice.date)) : serverTimestamp(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      const docRef = await addDoc(invoicesRef, invoiceData);
      await logAudit({ action: 'create', entityType: 'invoice', entityId: docRef.id, details: { vehicleId: invoice.vehicleId } });
      return { id: docRef.id, ...invoice };
    } catch (error) {
      console.error('Error adding invoice:', error);
      throw error;
    }
  },

  // Update an invoice (saves previous state to invoiceVersions subcollection before updating)
  async update(id, updates) {
    try {
      const invoiceRef = doc(db, COLLECTIONS.INVOICES, id);
      const snapshot = await getDoc(invoiceRef);
      if (snapshot.exists()) {
        const current = snapshot.data();
        const versionData = { ...current };
        if (versionData.date && versionData.date.toDate) versionData.date = versionData.date.toDate().toISOString();
        if (versionData.createdAt && versionData.createdAt.toDate) versionData.createdAt = versionData.createdAt.toDate().toISOString();
        if (versionData.updatedAt && versionData.updatedAt.toDate) versionData.updatedAt = versionData.updatedAt.toDate().toISOString();
        const versionsRef = collection(db, COLLECTIONS.INVOICES, id, 'invoiceVersions');
        await addDoc(versionsRef, { ...versionData, versionAt: serverTimestamp() });
      }
      await updateDoc(invoiceRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
      await logAudit({ action: 'update', entityType: 'invoice', entityId: id, details: { keys: Object.keys(updates) } });
      return { id, ...updates };
    } catch (error) {
      console.error('Error updating invoice:', error);
      throw error;
    }
  },

  // Delete an invoice
  async delete(id) {
    try {
      const invoiceRef = doc(db, COLLECTIONS.INVOICES, id);
      await deleteDoc(invoiceRef);
      await logAudit({ action: 'delete', entityType: 'invoice', entityId: id });
    } catch (error) {
      console.error('Error deleting invoice:', error);
      throw error;
    }
  },

  // Batch add invoices
  async batchAdd(invoices) {
    try {
      const batch = writeBatch(db);
      const invoicesRef = collection(db, COLLECTIONS.INVOICES);
      
      invoices.forEach(invoice => {
        const docRef = doc(invoicesRef);
        batch.set(docRef, {
          ...withCompany(invoice),
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      });

      await batch.commit();
      return invoices.length;
    } catch (error) {
      console.error('Error batch adding invoices:', error);
      throw error;
    }
  },

  // Delete all invoices (batch delete)
  async deleteAll() {
    try {
      const invoicesRef = collection(db, COLLECTIONS.INVOICES);
      const snapshot = await getDocs(companyQuery(invoicesRef));
      
      // Firestore batch limit is 500, so we need to process in batches
      const batchSize = 500;
      const docs = snapshot.docs;
      let deletedCount = 0;
      
      for (let i = 0; i < docs.length; i += batchSize) {
        const batch = writeBatch(db);
        const batchDocs = docs.slice(i, i + batchSize);
        
        batchDocs.forEach((docSnapshot) => {
          batch.delete(docSnapshot.ref);
        });
        
        await batch.commit();
        deletedCount += batchDocs.length;
      }
      
      return deletedCount;
    } catch (error) {
      console.error('Error deleting all invoices:', error);
      throw error;
    }
  },

  // Clean up old invoices (older than 1 year)
  async cleanupOld() {
    try {
      const oneYearAgo = new Date();
      oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
      oneYearAgo.setHours(0, 0, 0, 0);

      const allInvoices = await this.getAll();
      const oldInvoices = allInvoices.filter(inv => {
        const invDate = new Date(inv.date);
        invDate.setHours(0, 0, 0, 0);
        return invDate < oneYearAgo;
      });

      if (oldInvoices.length > 0) {
        const batch = writeBatch(db);
        oldInvoices.forEach(inv => {
          const invoiceRef = doc(db, COLLECTIONS.INVOICES, inv.id);
          batch.delete(invoiceRef);
        });
        await batch.commit();
        return oldInvoices.length;
      }
      return 0;
    } catch (error) {
      console.error('Error cleaning up old invoices:', error);
      throw error;
    }
  }
};

/**
 * Garage Info Service
 */
export const garageInfoService = {
  // Get garage info
  async get() {
    try {
      const garageInfoRef = doc(db, COLLECTIONS.GARAGE_INFO, currentCompanyName); // Isolated by company name as doc ID
      const snapshot = await getDoc(garageInfoRef);
      if (snapshot.exists()) {
        return snapshot.data();
      }
      return null;
    } catch (error) {
      console.error('Error fetching garage info:', error);
      throw error;
    }
  },

  // Set garage info
  async set(info) {
    try {
      const garageInfoRef = doc(db, COLLECTIONS.GARAGE_INFO, currentCompanyName);
      await setDoc(garageInfoRef, {
        ...info,
        companyId: currentCompanyName,
        updatedAt: serverTimestamp()
      }, { merge: true });
      await logAudit({ action: 'set', entityType: 'settings', entityId: 'garageInfo', details: {} });
      return info;
    } catch (error) {
      console.error('Error setting garage info:', error);
      throw error;
    }
  },

  // Update garage info
  async update(updates) {
    try {
      const garageInfoRef = doc(db, COLLECTIONS.GARAGE_INFO, currentCompanyName);
      await updateDoc(garageInfoRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
      await logAudit({ action: 'update', entityType: 'settings', entityId: 'garageInfo', details: { keys: Object.keys(updates) } });
      return updates;
    } catch (error) {
      console.error('Error updating garage info:', error);
      throw error;
    }
  },

  /** Real-time updates to garage info (theme, company details, etc.) for all signed-in clients. */
  subscribe(onData, onError) {
    const garageInfoRef = doc(db, COLLECTIONS.GARAGE_INFO, currentCompanyName);
    return onSnapshot(
      garageInfoRef,
      (snapshot) => {
        if (snapshot.exists()) {
          onData(snapshot.data());
        } else {
          onData(null);
        }
      },
      onError || ((e) => console.error('garageInfo subscribe error:', e))
    );
  }
};


/**
 * Ensure a Firestore user document exists at /users/{uid} with companyId.
 * Required for Firestore rules: vehicles/invoices are readable only when userDoc().companyId matches.
 * Call this after Firebase Auth login so every user can see company data.
 */
export async function ensureUserDoc(uid, { email = '', username = null } = {}) {
  const userRef = doc(db, COLLECTIONS.USERS, uid);
  const snap = await getDoc(userRef);
  if (!snap.exists()) {
    await setDoc(userRef, {
      companyId: currentCompanyName,
      email: email || '',
      username: username || (email && email.split('@')[0]) || '',
      role: 'sales',
      active: true,
      firebaseUid: uid,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    console.log('[Firestore] Created user doc at /users/' + uid + ' with companyId:', currentCompanyName);
  } else {
    const data = snap.data();
    if (!data.companyId || data.companyId !== currentCompanyName) {
      await updateDoc(userRef, { companyId: currentCompanyName, updatedAt: serverTimestamp() });
      console.log('[Firestore] Updated companyId on user doc /users/' + uid + ' to:', currentCompanyName);
    }
  }
}

/**
 * Users Service
 */
export const usersService = {
  // Get all users
  async getAll() {
    try {
      const usersRef = collection(db, COLLECTIONS.USERS);
      const snapshot = await getDocs(
        query(companyQuery(usersRef), orderBy('createdAt', 'desc'))
      );
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  },

  // Get a single user by ID
  async getById(id) {
    try {
      const userRef = doc(db, COLLECTIONS.USERS, id);
      const snapshot = await getDoc(userRef);
      if (snapshot.exists()) {
        const data = snapshot.data();
        if (data.companyId !== currentCompanyName && data.role !== 'admin') return null;
        return { id: snapshot.id, ...data };
      }
      return null;
    } catch (error) {
      console.error('Error fetching user:', error);
      throw error;
    }
  },

  // Get user by username
  async getByUsername(username) {
    try {
      console.log('usersService.getByUsername - Starting, username:', username);
      const usersRef = collection(db, COLLECTIONS.USERS);
      const q = query(usersRef, where('username', '==', username), where('companyId', '==', currentCompanyName), limit(1));
      console.log('usersService.getByUsername - Executing query...');
      const snapshot = await getDocs(q);
      console.log('usersService.getByUsername - Query completed, docs:', snapshot.docs.length);
      if (!snapshot.empty) {
        const doc = snapshot.docs[0];
        return { id: doc.id, ...doc.data() };
      }
      return null;
    } catch (error) {
      console.error('usersService.getByUsername - Error details:');
      console.error('  Code:', error.code);
      console.error('  Message:', error.message);
      console.error('  Full error:', error);
      throw error;
    }
  },

  // Get user by email
  async getByEmail(email) {
    try {
      console.log('usersService.getByEmail - Starting, email:', email);
      const usersRef = collection(db, COLLECTIONS.USERS);
      const q = query(usersRef, where('email', '==', email), where('companyId', '==', currentCompanyName), limit(1));
      console.log('usersService.getByEmail - Executing query...');
      const snapshot = await getDocs(q);
      console.log('usersService.getByEmail - Query completed, docs:', snapshot.docs.length);
      if (!snapshot.empty) {
        const doc = snapshot.docs[0];
        return { id: doc.id, ...doc.data() };
      }
      return null;
    } catch (error) {
      console.error('usersService.getByEmail - Error details:');
      console.error('  Code:', error.code);
      console.error('  Message:', error.message);
      console.error('  Full error:', error);
      throw error;
    }
  },

  // Add a new user
  async add(user) {
    try {
      // Ensure companyId is set so Firestore rules allow access to company data
      const result = await firebaseFunctions.createUser({
        ...user,
        companyId: user.companyId || currentCompanyName
      });
      return result.user;
    } catch (error) {
      console.error('usersService.add error:', error);
      throw error;
    }
  },

  // Update a user
  async update(id, updates) {
    try {
      await firebaseFunctions.updateUser(id, updates);
      return { id, ...updates };
    } catch (error) {
      console.error('usersService.update error:', error);
      throw error;
    }
  },

  // Delete a user
  async delete(id) {
    try {
      await firebaseFunctions.deleteUser(id);
    } catch (error) {
      console.error('usersService.delete error:', error);
      throw error;
    }
  }
};

/**
 * Role Permissions Service
 * Manages default role permissions stored in Firestore
 */
export const rolePermissionsService = {
  // Get default role permissions from Firestore
  async getDefaultRolePermissions() {
    try {
      const settingsRef = doc(db, COLLECTIONS.SETTINGS, currentCompanyName + '_rolePermissions'); // Isolated by company name
      const snapshot = await getDoc(settingsRef);
      if (snapshot.exists()) {
        return snapshot.data();
      }
      return null; // No custom permissions set, use defaults
    } catch (error) {
      console.error('Error fetching role permissions:', error);
      throw error;
    }
  },

  // Save default role permissions to Firestore
  async saveDefaultRolePermissions(permissions) {
    try {
      const settingsRef = doc(db, COLLECTIONS.SETTINGS, currentCompanyName + '_rolePermissions');
      await setDoc(settingsRef, {
        ...permissions,
        companyId: currentCompanyName,
        updatedAt: serverTimestamp()
      }, { merge: true });
      await logAudit({ action: 'update', entityType: 'settings', entityId: 'rolePermissions', details: {} });
      return permissions;
    } catch (error) {
      console.error('Error saving role permissions:', error);
      throw error;
    }
  }
};

/**
 * Promotions Service
 * Stores promotions per company in a single doc: promotions/{companyId}
 * Works for all companies (essexautocentre, dealer, tmuk) via companyId.
 */
export const promotionsService = {
  async get() {
    try {
      const ref = doc(db, COLLECTIONS.PROMOTIONS, currentCompanyName);
      const snapshot = await getDoc(ref);
      if (snapshot.exists()) {
        const data = snapshot.data();
        const items = data.items;
        return Array.isArray(items) ? items : [];
      }
      return [];
    } catch (error) {
      console.error('Error fetching promotions:', error);
      throw error;
    }
  },

  async set(items) {
    try {
      const ref = doc(db, COLLECTIONS.PROMOTIONS, currentCompanyName);
      await setDoc(ref, {
        companyId: currentCompanyName,
        items: Array.isArray(items) ? items : [],
        updatedAt: serverTimestamp()
      }, { merge: true });
      return items;
    } catch (error) {
      console.error('Error saving promotions:', error);
      throw error;
    }
  }
};

/**
 * Links Service (webLinks)
 * Stores links per company in a single doc: links/{companyId}
 * Works for all companies (essexautocentre, dealer, tmuk) via companyId.
 */
export const linksService = {
  async get() {
    try {
      const ref = doc(db, COLLECTIONS.LINKS, currentCompanyName);
      const snapshot = await getDoc(ref);
      if (snapshot.exists()) {
        const data = snapshot.data();
        const items = data.items;
        return Array.isArray(items) ? items : [];
      }
      return [];
    } catch (error) {
      console.error('Error fetching links:', error);
      throw error;
    }
  },

  async set(items) {
    try {
      const ref = doc(db, COLLECTIONS.LINKS, currentCompanyName);
      await setDoc(ref, {
        companyId: currentCompanyName,
        items: Array.isArray(items) ? items : [],
        updatedAt: serverTimestamp()
      }, { merge: true });
      return items;
    } catch (error) {
      console.error('Error saving links:', error);
      throw error;
    }
  }
};

/**
 * Audit Log Service
 * Who changed what, when. Stored in Firestore per company for vehicles, invoices, settings, users.
 */
export const auditLogService = {
  async add(payload) {
    return logAudit(payload);
  },

  async getRecent(limitCount = 100) {
    try {
      const ref = collection(db, COLLECTIONS.AUDIT_LOG);
      const q = query(
        companyQuery(ref),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => {
        const data = d.data();
        const createdAt = data.createdAt;
        return {
          id: d.id,
          ...data,
          createdAt: createdAt && typeof createdAt.toDate === 'function' ? createdAt.toDate().toISOString() : createdAt
        };
      });
    } catch (error) {
      console.error('Error fetching audit log:', error);
      throw error;
    }
  }
};

